import Button from "@assessment/components/atoms/button";
import { ProctoringFunctionsContext } from "@assessment/contexts/proctoringFunctionsContext";
import {
  useLazyFinishOngoingAssessmentQuery,
  useSaveCodeMutation,
  useExecuteTestCaseMutation,
  useSaveSubjectiveMutation,
  useStartQuestionMutation,
  useAttemptQuestionMutation
} from "@assessment/store/slices/api/assessmentApiSlice";
import { encodeToBase64 } from "@lxp-app/utils/encodeImperva.ts";
import { AssessmentSliceActions } from "@assessment/store/slices/assessmentSlice";
import { useAppDispatch, useAppSelector } from "@assessment/store/store";
import { exitFullScreen } from "@assessment/utils/global/fullScreenFunctions";
import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useShowErrors } from "./toasterWrapper";
import { BrowserProctoringFunctionsContext } from "@assessment/contexts/browserProctoringFunctionContext";
import orgBasedFeatureNameMapping from "@assessment/utils/orgBasedFeatureName";
import { saveAnswers } from "./saveAnswers";

const TimesUpModal = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const showErrors = useShowErrors();

  const [disabled, setDisabled] = useState(false);

  const {
    preAssessmentData,
    ongoingId,
    latestCodingState,
    assessmentData,
    candidateResponse: responses,
    candidateFibResponse: responsesFib
  } = useAppSelector((state) => state.AssessmentSlice);

  const { profile: user } = useAppSelector((state) => state.UserInfoSlice);

  const [finishOngoingAssessment] = useLazyFinishOngoingAssessmentQuery();
  const [saveCodeApi] = useSaveCodeMutation();
  const [executeTestCaseApi] = useExecuteTestCaseMutation();
  const [saveSubjectiveApi] = useSaveSubjectiveMutation();
  const [startQuestion] = useStartQuestionMutation();
  const [attemptQuestion] = useAttemptQuestionMutation();

  const { scheduleId } = useParams();
  const feedbackData = preAssessmentData?.socialInteractions;

  const showMarks = preAssessmentData?.showResults;
  const feedbackAllowed = preAssessmentData?.feedbackAllowed;

  const stateData = useRef<any>({});

  const context = useContext(ProctoringFunctionsContext);
  const browserContext = useContext(BrowserProctoringFunctionsContext);
  if (!context || !browserContext) {
    throw new Error("Context Error");
  }
  const { stopProctoring } = context;
  const { stopBrowserProctoring } = browserContext;

  const orgBasedFeatureName =
    orgBasedFeatureNameMapping[(user?.org as string) || "default"] ||
    orgBasedFeatureNameMapping["default"];

  const saveAllAnswersBeforeSubmit = async () => {
    try {
      if (!assessmentData?.ongoingSections) return;

      // Iterate through all sections and save all answers
      for (const section of assessmentData.ongoingSections) {
        for (const question of section.questions) {
          const questionMeta = {
            sectionId: section.sectionId,
            questionId: question.questionId
          };

          await saveAnswers(
            section,
            questionMeta,
            responses,
            ongoingId,
            dispatch,
            saveSubjectiveApi,
            startQuestion,
            saveCodeApi,
            attemptQuestion,
            responsesFib,
            false
          );
        }
      }
    } catch (err) {
      console.error("Error saving all answers before submit:", err);
    }
  };

  const assessmentSubmitHandler = async () => {
    try {
      setDisabled(true);

      if (!scheduleId) {
        throw {
          status: "",
          title: t("assessment.scheduleIdNotFound"),
          err: {},
          description: ""
        };
      }
      if (!ongoingId) {
        throw {
          status: "",
          title: t("assessment.ongoingIdNotFound"),
          err: {},
          description: ""
        };
      }

      // Save all pending answers when time expires
      await saveAllAnswersBeforeSubmit();

      if (latestCodingState) {
        try {
          const saveRes: any = await saveCodeApi({
            ongoingId,
            questionId: latestCodingState.questionId,
            quesVersion: latestCodingState.version,
            body: {
              code: encodeToBase64(latestCodingState.code),
              language: latestCodingState.language
            }
          });

          if (saveRes?.data?.data?.id) {
            await executeTestCaseApi({
              ongoingId,
              body: {
                questionId: latestCodingState.questionId,
                questionVersion: latestCodingState.version,
                savedCodeId: saveRes.data.data.id,
                hidden: true
              }
            });
          }
        } catch (err) {
          console.error("Save/execute before finish failed:", err);
        }
      }

      const res = await finishOngoingAssessment({
        ongoingId: ongoingId,
        scheduleId: scheduleId
      });

      if (res.isError) {
        throw {
          status: res.status,
          title: t("assessment.confirmModal.failedToFinish", {
            featureName: orgBasedFeatureName
          }),
          err: res.error,
          description: ""
        };
      }

      stopBrowserProctoring();
      const obtainedMarks = res?.data?.data?.obtainedMarks;
      const totalMarks = res?.data?.data?.totalMarks;
      const showScore = showMarks.includes("score") ? true : false;
      const showPercentage = showMarks.includes("percentage") ? true : false;
      const resultStatus = res?.data?.data?.resultStatus;
      stateData.current = {
        marksObtained: obtainedMarks,
        totalMarks: totalMarks,
        ongoingId: ongoingId,
        feedbackData: feedbackData,
        showScore: showScore,
        showPercentage: showPercentage,
        showMarks: showMarks,
        feedbackAllowed: feedbackAllowed,
        feedbackId: feedbackData[0]?.contentId,
        resultStatus: resultStatus
      };
      stopProctoring();
      exitFullScreen(dispatch);
      setDisabled(false);
    } catch (err) {
      console.error(err);
      const temp: any = err;
      const rawErrList = temp?.err?.data?.error;
      const errList =
        rawErrList?.length > 0
          ? rawErrList?.map((el: string) => el.split("_")[0])
          : [];
      if (showErrors)
        showErrors(temp?.title, errList, "error", temp?.description);
      setDisabled(false);
    }
  };

  useEffect(() => {
    assessmentSubmitHandler();
  }, []);

  return (
    <div>
      <div className="mt-5">
        {t("assessment.timesUp.autoSubmittedMessage", {
          featureName: orgBasedFeatureName
        })}
      </div>
      <div className="flex justify-end gap-5 mt-10">
        {/* {preAssessmentData?.showResults != "noresult" && ( */}
        <Button
          label={t("common.ok")}
          variant="contained"
          disable={disabled}
          onClick={() => {
            navigate(
              `/assessment/feedback/${scheduleId}?ongoingId=${ongoingId}`,
              { state: { ...stateData.current } }
            );
            dispatch(AssessmentSliceActions.resetState());
          }}
        />
        {/* )} */}
      </div>
    </div>
  );
};

export default TimesUpModal;
